import React, { useCallback, useState } from 'react';
import {
  AlertTriangle, ExternalLink, FileCode2, History, Loader2, RotateCcw,
} from 'lucide-react';
import client from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ConfirmDialog from './ConfirmDialog';
import { CUSTOM_CODE_SLOTS } from '../lib/customCode';
import {
  ACCOUNT_PAGES, ACCOUNT_PARTS, BONUS_PARTS, GAME_CHOICE_PARTS, GAME_PAGE_PARTS, SECTIONS,
} from '../../lib/domHooks';

const errorText = (error, fallback) => (
  error?.response?.data?.detail || fallback
);

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatLength = (length) => {
  const value = Number(length || 0);
  if (!value) return 'boş';
  if (value < 1024) return `${value} karakter`;
  return `${(value / 1024).toFixed(1)} KB`;
};

/** Bir slotun sürüm geçmişi — kapalı gelir ve açılmadan veri çekmez.
 *
 * Liste her gün bakılan bir şey değil, "kaydettim, site dağıldı" dendiğinde
 * açılan bir kutudur; sekmenin açılış maliyetine girmemelidir (çapa
 * kuyruğundaki kararın aynısı).
 */
const RevisionList = ({ slot, activeVersion, onRestored }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`/admin/custom-code/${slot}/revisions`);
      setItems(data.items || []);
    } catch (error) {
      toast({ title: 'Sürümler yüklenemedi', description: errorText(error, 'Lütfen tekrar deneyin.') });
    } finally {
      setLoading(false);
    }
  }, [slot, toast]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && items === null) load();
  };

  const restore = async () => {
    const revision = pending;
    setPending(null);
    if (!revision) return;
    try {
      const { data } = await client.post(
        `/admin/custom-code/revisions/${revision.id}/restore`,
      );
      toast({ title: `${revision.version}. sürüm geri yüklendi` });
      await load();
      onRestored?.(data);
    } catch (error) {
      toast({ title: 'Geri yüklenemedi', description: errorText(error, 'Lütfen tekrar deneyin.') });
    }
  };

  return (
    <div className="mt-3">
      <button type="button" onClick={toggle} className="admin-btn admin-btn-secondary text-xs">
        <History size={13} />
        {open ? 'Sürüm geçmişini kapat' : 'Sürüm geçmişi'}
      </button>

      {open ? (
        <div className="mt-2 overflow-hidden rounded-lg border border-white/10">
          {loading ? (
            <div className="flex items-center gap-2 p-3 text-xs text-slate-400">
              <Loader2 size={13} className="animate-spin" /> Yükleniyor…
            </div>
          ) : null}
          {!loading && items && !items.length ? (
            <div className="p-3 text-xs text-slate-500">Henüz kayıt yok.</div>
          ) : null}
          {!loading && items?.map((item) => {
            const current = item.version === activeVersion;
            return (
              <div
                key={item.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-white/5 px-3 py-2 text-xs last:border-b-0"
              >
                <span className="font-semibold text-slate-200">
                  {item.version}. sürüm
                </span>
                {current ? (
                  <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[11px] text-emerald-300">
                    yayında
                  </span>
                ) : null}
                <span className="text-slate-500">{formatLength(item.length)}</span>
                <span className="text-slate-500">{formatDate(item.created_at)}</span>
                {item.admin_username ? (
                  <span className="text-slate-500">{item.admin_username}</span>
                ) : null}
                {item.note ? (
                  <span className="text-slate-400">{item.note}</span>
                ) : null}
                {!current ? (
                  <button
                    type="button"
                    onClick={() => setPending(item)}
                    className="admin-btn admin-btn-secondary ml-auto text-xs"
                  >
                    <RotateCcw size={12} /> Geri yükle
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(pending)}
        title={`${pending?.version || ''}. sürümü geri yükle`}
        description={
          'Bu sürümün içeriği yeni bir sürüm olarak yayına alınır; şu anda '
          + 'yayında olan metin geçmişte kalır. Kaydedilmemiş değişiklikleriniz '
          + 'varsa kaybolur.'
        }
        confirmLabel="Geri yükle"
        onConfirm={restore}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};

/**
 * Özel kod bölümü (Ayarlar → Özel Kod).
 *
 * Parçalar `settings` kaydında **değildir**: kendi tablosundadır, kendi
 * ucundan yazılır ve her kayıt geri alınabilir bir sürüm bırakır (bkz.
 * `backend/services/custom_code.py`). CSS ve JS tarayıcıya **dosya olarak**
 * servis edilir; adres içeriğin özetini taşıdığı için önbellek elle
 * temizlenmeden tazelenir.
 *
 * Ayrım yöneticiye de söylenir: stil değişikliği sayfa yenilemesi istemez,
 * satır içi HTML ister. İkisi aynı uyarıyla anlatılsaydı yönetici her CSS
 * düzeltmesinden sonra boşuna sayfayı yenilerdi.
 */
const CustomCodePanel = ({ value, meta, onChange, onRefresh, readOnly = false }) => {
  // Seçici listesi kapalı gelir: her gün bakılan bir şey değil, stil
  // yazarken bir kez açılıp kopyalanan bir kutudur.
  const [selectors, setSelectors] = useState(false);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-300">
          <AlertTriangle size={15} /> Bu kodlar sitede çalıştırılır
        </div>
        <p className="mt-2 text-xs leading-5 text-slate-400">
          HTML, CSS ve JavaScript çalıştırılır. Hatalı veya güvenilmeyen kod siteyi
          bozabilir; yalnızca kaynağını bildiğiniz kodları ekleyin. Kodlar
          <b className="text-slate-300"> yalnızca oyuncu portalında</b> çalışır,
          yönetim panelinde çalışmaz.
        </p>
        <p className="mt-2 text-xs leading-5 text-slate-400">
          <b className="text-slate-300">CSS değişikliği sayfa yenilemesi istemez</b> —
          stil ayrı bir dosya olarak yüklenir ve adresi her kayıtta tazelenir.
          Satır içi HTML alanlarında ise değişiklikten sonra
          <b className="text-slate-300"> açık oyuncu sayfalarını yenileyin</b>: bir kez
          çalışmış scriptin bıraktıkları (canlı destek penceresi, izleme kuyruğu)
          geri alınamaz.
        </p>
        <p className="mt-2 text-xs leading-5 text-slate-400">
          Her kayıt bir sürüm bırakır; yanlış bir kod siteyi bozarsa alanın
          altındaki <b className="text-slate-300">Sürüm geçmişi</b>nden geri
          dönebilirsiniz.
        </p>
      </div>

      <div className="admin-fieldset">
        <button
          type="button"
          onClick={() => setSelectors((open) => !open)}
          className="admin-btn admin-btn-secondary text-xs"
        >
          <FileCode2 size={13} />
          {selectors ? 'Seçici listesini kapat' : 'CSS seçici listesi (sitedeki tanımlı alanlar)'}
        </button>
        {selectors ? (
          <div className="mt-3 space-y-2 text-xs leading-5 text-slate-400">
            <p>
              Sitenin ana bölgelerinde sabit kimlikler vardır; tema değişse de
              bunlar değişmez. Tailwind sınıflarına değil bunlara yazın.
            </p>
            <pre className="settings-code-block">
{`#bhs-header        üst menü / header
#bhs-nav           kategori barı
#bhs-footer        footer
#bhs-bottom-bar    mobil alt menü
#bhs-main          içerik alanı

[data-bhs="game-card"]      oyun kartı
[data-bhs="game-grid"]      oyun ızgarası
[data-bhs="slider"]         banner slider
[data-bhs="category-card"]  kategori kartı
[data-bhs-device="mobile"]  kategori alanının mobil seti (Betrun; "desktop" da olur)
#not-logged-welcome        giriş yapmamış ziyaretçiye ana sayfanın en üstündeki boş alan (Betrun)
[data-bhs="bet-slip"]       kupon paneli
[data-bhs="auth-modal"]     giriş/kayıt penceresi
[data-bhs="game-choice-modal"]  oyuna basınca açılan seçim penceresi
[data-bhs="section"]        ana sayfadaki bütün bölümler
[data-bhs-section="faq"]    tek bir bölüm (aşağıdaki adlar)
[data-bhs="content-page"]   footer'daki içerik sayfası (/sayfa/…)
[data-bhs-slug="iletisim"]  tek bir içerik sayfası

body[data-bhs-page="slot"]  yalnızca slot sayfasında
html[data-site-theme="vega"]  yalnızca Vega şemasında

[data-bhs="account"]                   hesap sayfalarının kökü (/hesap/…)
[data-bhs-account-page="profile"]      tek bir hesap sayfası (aşağıdaki adlar)
[data-bhs-account="wager"]             hesap sayfasındaki bir alan (aşağıdaki adlar)
[data-bhs-field="email"]               formdaki tek bir alan
[data-bhs-status="onaylandi"]          durum taşıyan satır / rozet

[data-bhs="bonus-card"]                bonus kampanyası kartı (Bonuslar sayfası)
[data-bhs="bonus-modal"]               bonus detay penceresi
[data-bhs-bonus="headline"]            kart / penceredeki alan (aşağıdaki adlar)
[data-bhs-bonus-id="…"]                tek bir kampanyanın kartı ve penceresi

[data-bhs="game-page"]                 oyunun kendi sayfası (/oyun/…, oyunu sayfada açan şemada)
[data-bhs-game-page="sidebar"]         oyun sayfasındaki alan (aşağıdaki adlar)`}
            </pre>
            <p>
              Sayfaya özel kural yazmak için <code>body[data-bhs-page="…"]</code>
              {' '}öneki kullanılır; değer adres çubuğundaki sayfa adıdır
              (<code>home</code>, <code>slot</code>, <code>live-casino</code>,
              {' '}<code>sports</code>, <code>promotions</code>, <code>account</code>,
              {' '}<code>game</code>).
            </p>
            <p>
              Bölüm adları (<code>data-bhs-section</code>) başlıktan değil
              işlevden gelir; şema değişse de aynıdır:{' '}
              {Object.values(SECTIONS).map((name, index) => (
                <React.Fragment key={name}>
                  {index > 0 ? ', ' : ''}
                  <code>{name}</code>
                </React.Fragment>
              ))}.
            </p>
            <p>
              Bonus alanları (<code>data-bhs-bonus</code>) kartta ve detay
              penceresinde aynıdır; yalnızca birine yazmak için önüne kök eklenir
              (<code>[data-bhs="bonus-modal"] [data-bhs-bonus="headline"]</code>):{' '}
              {BONUS_PARTS.map((name, index) => (
                <React.Fragment key={name}>
                  {index > 0 ? ', ' : ''}
                  <code>{name}</code>
                </React.Fragment>
              ))}.
            </p>
            <p>
              Oyun seçim penceresi alanları (<code>data-bhs-game-choice</code>);
              pencere giriş yapmamış ziyaretçide{' '}
              <code>[data-bhs-game-choice-state="guest"]</code>, üyede{' '}
              <code>"member"</code> taşır:{' '}
              {GAME_CHOICE_PARTS.map((name, index) => (
                <React.Fragment key={name}>
                  {index > 0 ? ', ' : ''}
                  <code>{name}</code>
                </React.Fragment>
              ))}.
            </p>
            <p>
              Oyun sayfası alanları (<code>data-bhs-game-page</code>):{' '}
              {GAME_PAGE_PARTS.map((name, index) => (
                <React.Fragment key={name}>
                  {index > 0 ? ', ' : ''}
                  <code>{name}</code>
                </React.Fragment>
              ))}.
            </p>
            <p>
              Hesap sayfaları (<code>data-bhs-account-page</code>):{' '}
              {Object.values(ACCOUNT_PAGES).map((name, index) => (
                <React.Fragment key={name}>
                  {index > 0 ? ', ' : ''}
                  <code>{name}</code>
                </React.Fragment>
              ))}.
            </p>
            <p>
              Hesap alanları (<code>data-bhs-account</code>) sayfalar arasında
              ortaktır; sayfaya özel kural için önüne sayfa adı eklenir
              (<code>[data-bhs-account-page="deposit"] [data-bhs-account="panel"]</code>):{' '}
              {ACCOUNT_PARTS.map((name, index) => (
                <React.Fragment key={name}>
                  {index > 0 ? ', ' : ''}
                  <code>{name}</code>
                </React.Fragment>
              ))}.
            </p>
          </div>
        ) : null}
      </div>

      {CUSTOM_CODE_SLOTS.map((slot) => {
        const info = meta?.[slot.key];
        return (
          <div key={slot.key} className="admin-fieldset">
            <div className="admin-fieldset-title flex flex-wrap items-center gap-2">
              <span>{slot.title}</span>
              {slot.kind === 'asset' ? (
                <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[11px] font-normal text-sky-300">
                  dosya olarak yüklenir
                </span>
              ) : null}
              {info?.version ? (
                <span className="text-[11px] font-normal text-slate-500">
                  {info.version}. sürüm · {formatDate(info.updated_at)}
                  {info.admin_username ? ` · ${info.admin_username}` : ''}
                </span>
              ) : null}
            </div>
            <p className="-mt-2 mb-3 text-xs leading-5 text-slate-500">{slot.hint}</p>
            <textarea
              value={value?.[slot.key] || ''}
              onChange={(event) => onChange(slot.key, event.target.value)}
              className="admin-input settings-code"
              spellCheck={false}
              placeholder={slot.placeholder}
              aria-label={slot.title}
              readOnly={readOnly}
            />
            {info?.url ? (
              <a
                href={info.url}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-sky-300 hover:underline"
              >
                <ExternalLink size={12} /> Yayındaki dosyayı aç
              </a>
            ) : null}
            <RevisionList
              slot={slot.key}
              activeVersion={info?.version || 0}
              onRestored={onRefresh}
            />
          </div>
        );
      })}
    </div>
  );
};

export default CustomCodePanel;
